import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { autofill, getFormValues } from 'redux-form'
import { batch, useDispatch, useSelector } from 'react-redux'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// constants
import { Forms } from 'constants/forms'
// graphql
import { FIELD_VALUE_DELETE_MUTATION } from 'graphql/objects/mutations'
// types
import {
  IField,
  FieldType,
  IMutationResponse,
  IRiskTemplateFormCondition,
  IFieldValueDeleteMutationArgs,
  RiskTemplateFormConditionActionType
} from 'types/graphql'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
// utils
import { isEmpty } from 'lodash'
import { handleError } from 'utils/errors/handlers'
import { getShouldExecute } from 'hooks/risks/useLogicalConditions/utils'

interface Props {
  fields: IField[]
  isRisk: boolean
  formConditions: IRiskTemplateFormCondition[]
}

export const useLogicalConditions = ({ fields, isRisk, formConditions }: Props): IField[] => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { client } = useApolloClient()

  const formValues = useSelector((state: any) => getFormValues(Forms.RISK_TEMPLATE_FORM)(state))

  const { risk, riskRefetch } = React.useContext(RiskAPIContext)

  const [preparedFields, setPreparedFields] = React.useState([])

  const deleteFieldValues = async (ids: string[]) => {
    try {
      await client.mutate<IMutationResponse, IFieldValueDeleteMutationArgs>({
        mutation: FIELD_VALUE_DELETE_MUTATION,
        variables: { ids }
      })
    } catch (errors) {
      handleError(errors, 'RiskTemplateAPI.deleteFieldValues')
    }
  }

  const applyConditions = (
    field: IField,
    formValues: Record<string, any>,
    conditions: IRiskTemplateFormCondition[]
  ) => {
    const formattedField = { ...field }

    if (conditions.every((condition) => condition.actions.every((action) => action.fieldId !== field.id))) {
      return formattedField
    }

    conditions?.forEach((condition) => {
      const fieldValue = formValues[condition.fieldId]?.value

      if (condition.actions.every((action) => action.fieldId !== field.id)) {
        return
      }

      const shouldExecute = getShouldExecute(condition, fieldValue)

      if (shouldExecute) {
        condition.actions.forEach((action) => {
          if (action.fieldId === field.id) {
            if (action.type === RiskTemplateFormConditionActionType.LimitResponse) {
              formattedField.limitResponse = { value: action.value }
            }

            if (action.type === RiskTemplateFormConditionActionType.Visibility) {
              formattedField.hidden = action.value
            }

            if (action.type === RiskTemplateFormConditionActionType.LimitOptions) {
              formattedField.options = field.options.map((option) => {
                const formattedOption = formattedField.options.find((i) => i.value === option.value)
                // @ts-ignore
                const isOptionShownByCond = typeof formattedOption?.hidden === 'boolean' && !formattedOption?.hidden

                if (isOptionShownByCond || action.value.includes(option.id)) {
                  return { ...option, hidden: false }
                }

                return { ...option, hidden: true }
              })
            }
          }
        })
      }
    })

    return formattedField
  }

  const updateFields = () => {
    const formattedFields = fields.map((field) => applyConditions(field, formValues, formConditions))

    let preparedFields = formattedFields
    let curFormValues = formValues
    let prevFormValues

    do {
      prevFormValues = curFormValues

      const visibleFields = preparedFields.filter((item) => {
        const isVisible = !item.hidden
        const isParentVisible = item.parentFieldId
          ? preparedFields.find((f) => f.id === item.parentFieldId)?.hidden === false
          : true

        return isVisible && isParentVisible
      })

      curFormValues = Object.fromEntries(
        Object.entries(curFormValues).filter(([key]) => visibleFields.some((f) => f.id === key))
      )

      preparedFields = fields.map((field) => {
        const formattedField = formattedFields.find((f) => f.id === field.id)

        return applyConditions(
          {
            ...field,
            options: formattedField?.options || field.options
          },
          curFormValues,
          formConditions
        )
      })
    } while (JSON.stringify(curFormValues) !== JSON.stringify(prevFormValues))

    setPreparedFields(preparedFields)
  }

  const clearFieldsValues = async () => {
    const fieldIds = preparedFields
      .filter((f) => f.hidden)
      .flatMap((f) => {
        if (f.fieldType === FieldType.Section || f.fieldType === FieldType.Table) {
          const children = preparedFields.filter(({ parentFieldId }) => parentFieldId === f.id)

          return [f, ...children]
        }

        return f
      })
      .map((f) => f.id)

    const fieldValueIds = fieldIds
      .map(
        (id) => risk.fieldValueMap?.find((item) => item.fieldId === id)?.fieldValueId || formValues?.[id]?.fieldValueId
      )
      .filter((id) => id)

    if (fieldValueIds.length) {
      await deleteFieldValues(fieldValueIds)

      batch(() => fieldIds.forEach((id) => dispatch(autofill(Forms.RISK_TEMPLATE_FORM, id, undefined))))

      riskRefetch()
    }
  }

  React.useEffect(() => {
    if (isRisk) {
      setPreparedFields([])
    }
  }, [risk?.id])

  React.useEffect(() => {
    if (formConditions?.length && fields?.length && isRisk && !isEmpty(formValues) && !router.query.archived) {
      updateFields()
    }
  }, [JSON.stringify(formValues)])

  React.useEffect(() => {
    if (isRisk && preparedFields.length) {
      clearFieldsValues()
    }
  }, [JSON.stringify(preparedFields)])

  return isRisk && preparedFields.length
    ? preparedFields.filter((f) => !f.hidden)
    : isRisk
      ? fields?.filter((f) => !f.hidden)
      : fields
}
